import { CreditsPlusIcon } from '@components/Icons/CreditsPlusIcon';
import { getPlanCreditGroups } from '@lib/planFeatures';
import { formatCreditsWithUnit } from '@lib/utils/units';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded';
import { Product } from '@stores/Billing';
import React from 'react';
import css from './PlanFeatures.module.scss';

type PlanFeaturesProps = {
  product: Product;
  /**
   * Whether the per-feature credit breakdown is shown. Wide layouts always show it; stacked
   * ones collapse it behind "Show all features" on every card but the recommended one.
   */
  expanded: boolean;
  onExpand: () => void;
  /** The `*` after each breakdown label; scrolls to the page's "amounts are approximate" note. */
  onApproxClick?: () => void;
};

/**
 * The feature list inside a paid plan's card — what the plan includes, then what its credits
 * convert to — rendered identically on the Pricing page and the paywall.
 */
export const PlanFeatures = ({ product, expanded, onExpand, onApproxClick }: PlanFeaturesProps) => (
  <div className={css.subscriptionFeatures}>
    <div className={css.featureLine}>
      <AutoAwesomeIcon className={css.featureIcon} />
      <span>
        Access to <strong>StarZero Agents</strong>
      </span>
    </div>

    <div className={css.featureLine}>
      <CloudQueueRoundedIcon className={css.featureIcon} />
      <span>
        <strong>{product.quota.storageHours} hours</strong> of storage
      </span>
    </div>

    <div className={css.featureLine}>
      <span className={css.featureIconWrap}>
        <CreditsPlusIcon />
      </span>
      <span>
        <strong>{formatCreditsWithUnit(product.quota.credits)}</strong> per {product.price.interval}
      </span>
    </div>

    {expanded ? (
      <>
        <div className={css.featureSeparator} />

        {getPlanCreditGroups(product.quota.credits).map((group) => (
          <React.Fragment key={group.label}>
            <div className={css.creditsBreakdownLabel}>
              {group.label}
              <button
                type="button"
                className={css.approxMarker}
                onClick={onApproxClick}
                aria-label="Amounts are approximate — see note below"
              >
                *
              </button>
            </div>
            <div className={css.creditsBreakdown}>
              {group.items.map(({ Icon, content }, i) => (
                <div key={i} className={css.creditUsageLine}>
                  <Icon className={css.creditUsageIcon} />
                  <span>{content}</span>
                </div>
              ))}
            </div>
          </React.Fragment>
        ))}
      </>
    ) : (
      <button type="button" className={css.showAllFeatures} onClick={onExpand}>
        Show all features
      </button>
    )}
  </div>
);
